import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, AlertTriangle, CheckCircle2, Search, ShieldAlert, Check } from "lucide-react";
import AnalystGlyph from "@/components/analyst/AnalystGlyph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-[hsl(var(--border))]/60 last:border-0">
      <div>
        <p className="text-[13px] font-semibold">{label}</p>
        {hint && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

const FLAG_LABEL = {
  data_extraction: "Data extraction", permission_probing: "Access probing", prompt_attack: "Prompt attack",
  secrets: "Secrets", deception: "Deception", people_judgement: "Judging people", off_topic: "Off-topic",
};

function FlagPill({ screen, onReview }) {
  const done = screen.reviewed;
  const tone = done ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
    : screen.severity === "high" ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900"
      : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900";
  return (
    <div className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-px text-[10px] font-semibold ${tone}`}
        title={screen.reason} data-testid="ai-audit-flag">
        <ShieldAlert className="w-3 h-3" />{FLAG_LABEL[screen.category] || "Flagged"}{screen.severity === "high" && !done ? " · high" : ""}
      </span>
      <button onClick={onReview} data-testid="ai-audit-review-btn"
        className="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
        title={done ? `Reviewed${screen.reviewed_by ? ` by ${screen.reviewed_by}` : ""} — click to reopen` : "Mark as reviewed"}>
        <Check className="w-3 h-3" />{done ? "Reviewed" : "Mark reviewed"}
      </button>
    </div>
  );
}

function UsageAudit({ usage, query, setQuery, flagged, setFlagged, onReview }) {
  if (!usage) return null;
  return (
    <div className="space-y-3">
      <p className="text-[12px]" data-testid="ai-usage-company-total">
        Today ({usage.date}): <strong>{usage.company_total}</strong> questions company-wide
        {usage.cost_today_usd != null && <> · est. <strong>${usage.cost_today_usd.toFixed(3)}</strong> today</>}
        {usage.per_user.length > 0 && <> · {usage.per_user.map(u => `${u.user} ${u.count}`).join(", ")}</>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search questions, people, clients"
            className="w-64 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] pl-7 pr-2 py-1 text-[12px]"
            data-testid="ai-audit-search" />
        </label>
        <button onClick={() => setFlagged(!flagged)} data-testid="ai-audit-flagged-toggle"
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${flagged
            ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900"
            : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
          <ShieldAlert className="w-3.5 h-3.5" />Flagged only
          {usage.flagged_open > 0 && <span className="ml-0.5 rounded-full bg-red-600 px-1.5 text-[10px] text-white">{usage.flagged_open}</span>}
        </button>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
          Every typed question is checked for misuse in the background by Haiku. Questions are kept for 180 days.
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
        <table className="w-full text-[11px]" data-testid="ai-audit-table">
          <thead className="bg-[hsl(var(--muted))]/60">
            <tr>{["When", "User", "Question", "Model", "What", "Input", "Cached", "Output", "Est. cost", "Status", "Code runs", "Records", "Lookups", "Conversation"].map(h =>
              <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {usage.audit.length === 0 && <tr><td colSpan={14} className="px-2 py-3 text-center text-[hsl(var(--muted-foreground))]">{query || flagged ? "Nothing matches" : "No questions yet"}</td></tr>}
            {usage.audit.map(a => (
              <tr key={a.id} className={`border-t border-[hsl(var(--border))] align-top ${a.screen?.flag && !a.screen.reviewed ? "bg-red-50/50 dark:bg-red-950/20" : ""}`}>
                <td className="px-2 py-1.5 whitespace-nowrap">{new Date(a.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{a.user_name}</td>
                <td className="px-2 py-1.5 min-w-[280px] max-w-[360px]" data-testid="ai-audit-question">
                  {a.screen?.flag && <FlagPill screen={a.screen} onReview={() => onReview(a.id, !a.screen.reviewed)} />}
                  <div className="line-clamp-2" title={a.question || ""}>{a.question || <span className="text-[hsl(var(--muted-foreground))]">—</span>}</div>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap" title={a.route_reason || ""}>{a.routed_model || (a.model || "—").replace("claude-", "")}</td>
                <td className="px-2 py-1.5 max-w-[200px] truncate" title={a.email_to ? `Emailed to ${a.email_to.join(", ")}` : ""}>
                  {a.email_to ? `Email · ${a.title}` : a.chip ? `${a.chip.replace(/_/g, " ")}${a.client ? ` · ${a.client}` : ""}` : a.audience === "customer" ? `Customer · ${a.client}` : "Question"}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums" data-testid="ai-audit-input">{a.tokens.input ?? "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums" data-testid="ai-audit-cached">{a.tokens.cache_read ?? "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums" data-testid="ai-audit-output">{a.tokens.output ?? "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap" data-testid="ai-audit-cost"
                  title={a.tokens.cost_estimated ? "Estimated — asked before cost tracking (no caching)" : ""}>
                  {a.tokens.cost_usd != null ? `${a.tokens.cost_estimated ? "~" : ""}$${a.tokens.cost_usd.toFixed(4)}` : "—"}
                </td>
                <td className="px-2 py-1.5">{a.status}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{a.code_runs ?? "—"}</td>
                <td className="px-2 py-1.5 font-mono" title={(a.tools || []).flatMap(t => t.ids).join(", ")}>{(a.tools || []).reduce((n, t) => n + t.ids.length, 0)}</td>
                <td className="px-2 py-1.5 max-w-[220px]">{(a.tools || []).map((t, i) => {
                  const full = `${t.name}(${JSON.stringify(t.args)})`;
                  return <div key={i} className="font-mono truncate" title={full}>{full}</div>;
                })}</td>
                <td className="px-2 py-1.5 font-mono">{a.conversation_id?.slice(0, 8)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AiSettingsPanel() {
  const [s, setS] = useState(null);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [flagged, setFlagged] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ai/settings`).then(r => setS(r.data)).catch(() => toast.error("Could not load Analyst settings"));
  }, []);
  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/usage`, { params: { q: query.trim() || undefined, flagged: flagged || undefined } });
      setUsage(data);
    } catch { toast.error("Could not load the Analyst audit log"); }
  }, [query, flagged]);
  useEffect(() => { const t = setTimeout(loadUsage, 300); return () => clearTimeout(t); }, [loadUsage]);

  const review = async (id, reviewed) => {
    try {
      await axios.patch(`${API}/ai/audit/${id}/review`, null, { params: { reviewed } });
      loadUsage();
    } catch { toast.error("Could not update the review"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { roles_enabled, daily_limit, company_cap_enabled, company_cap, digest_summary_enabled, opus_daily_cap } = s;
      const { data } = await axios.put(`${API}/ai/settings`, { roles_enabled, daily_limit: Number(daily_limit), company_cap_enabled, company_cap: Number(company_cap), digest_summary_enabled, opus_daily_cap: Number(opus_daily_cap) });
      setS(v => ({ ...v, ...data })); toast.success("Analyst settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;
  const set = (k, v) => setS(o => ({ ...o, [k]: v }));
  const num = "w-24 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2 py-1 text-[13px] text-right";

  return (
    <div className="space-y-5 py-2" data-testid="ai-settings-panel">
      <div className="flex items-center gap-2">
        <span className="text-orange-500"><AnalystGlyph size={15} /></span>
        <p className="text-sm font-bold">Analyst</p>
        {s.configured
          ? <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-emerald-600" data-testid="ai-key-status"><CheckCircle2 className="w-3.5 h-3.5" />Anthropic key set</span>
          : <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid="ai-key-status"><AlertTriangle className="w-3.5 h-3.5" />AI is not set up</span>}
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1">Who can use it</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {s.roles.map(r => (
            <label key={r} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-[12px] cursor-pointer">
              <Switch checked={!!s.roles_enabled[r]} onCheckedChange={v => set("roles_enabled", { ...s.roles_enabled, [r]: v })} data-testid={`ai-role-toggle-${r}`} />{r}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Row label="Daily questions per user" hint="Resets at midnight IST. Admins count too.">
          <input type="number" min={1} value={s.daily_limit} onChange={e => set("daily_limit", e.target.value)} className={num} data-testid="ai-daily-limit-input" />
        </Row>
        <Row label="Company-wide daily cap" hint="All users together share this number.">
          <div className="flex items-center gap-2">
            <input type="number" min={1} value={s.company_cap} disabled={!s.company_cap_enabled} onChange={e => set("company_cap", e.target.value)} className={`${num} disabled:opacity-40`} data-testid="ai-company-cap-input" />
            <Switch checked={s.company_cap_enabled} onCheckedChange={v => set("company_cap_enabled", v)} data-testid="ai-company-cap-toggle" />
          </div>
        </Row>
        <Row label="“Think deeper with Opus” per user per day" hint="Analyst answers with Haiku or Sonnet; Opus runs only when someone chooses it on a complex question. Beyond this limit, it answers with Sonnet.">
          <input type="number" min={0} max={50} value={s.opus_daily_cap ?? 5} onChange={e => set("opus_daily_cap", e.target.value)} className={num} data-testid="ai-opus-cap-input" />
        </Row>
        <Row label="“Today at a glance” in Daily Digest" hint="3–5 sentence AI summary at the top of the 12:00 IST owner digest.">
          <Switch checked={s.digest_summary_enabled} onCheckedChange={v => set("digest_summary_enabled", v)} data-testid="ai-digest-summary-toggle" />
        </Row>
      </div>

      <button onClick={save} disabled={saving} data-testid="ai-settings-save-btn"
        className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-orange-600 disabled:opacity-50 transition-colors">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save Analyst settings
      </button>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Usage & audit log</p>
        <UsageAudit usage={usage} query={query} setQuery={setQuery} flagged={flagged} setFlagged={setFlagged} onReview={review} />
      </div>
    </div>
  );
}
